import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEffect, useId, useRef, useState } from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import replaceUserTags from "utils/replaceUserTags";
import { Tooltip } from "@dialogueconsulting/sked-ui";

import { useTrackEvent } from "libs/analytics";
import {
  COLLABORATION_VISIBILITY,
  COLLABORATION_VISIBILITY_COPY,
  CollaborationVisibility,
} from "./constants";
import { ColCollaborator, ColComment } from "../types";
import { User } from "@/features/user";

const MENTION_ITEM_HEIGHT = 30;

/**
 * component for textarea and send button behaviours in collaboration chat
 */
function CollaborationInputControls({
  collaborators,
  onMessageSend,
  editingComment,
  disabled,
  currentUser,
  onCancelEdit,
}: {
  collaborators: ColCollaborator[];
  onMessageSend: (message: string, visibility: CollaborationVisibility) => void;
  editingComment?: ColComment | null;
  disabled?: boolean;
  currentUser: User;
  onCancelEdit?: () => void;
}) {
  const trackEvent = useTrackEvent();

  // values for mention dropdown
  const [availableCollaborators, setAvailableCollaborators] = useState<
    ColCollaborator[]
  >([]);
  // highlighted user in dropdown
  const [selectedCollaborator, setSelectedCollaborator] = useState<
    number | null
  >(null);
  const [inputValue, setInputValue] = useState("");
  const [visibility, setVisibility] = useState<CollaborationVisibility>(
    COLLABORATION_VISIBILITY.INTERNAL
  );
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mentionListRef = useRef<HTMLUListElement>(null);

  const visibilityCopy = COLLABORATION_VISIBILITY_COPY[visibility];
  const toggleId = useId();

  const displayName =
    [currentUser?.firstName, currentUser?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim()
      .replaceAll(/\s{2,}/g, " ") ||
    currentUser?.userName ||
    currentUser?.email ||
    "you";

  const handleValueChange = (value: string) => {
    setInputValue(value);
    const parsedMentionString = value.match(/@\w*$/);
    if (parsedMentionString) {
      const username = parsedMentionString[0].slice(1).toLowerCase();
      const newCollaborators = collaborators.filter((user) =>
        user.username?.toLowerCase().startsWith(username)
      );
      if (availableCollaborators.length === 0 && newCollaborators.length > 0) {
        setSelectedCollaborator(0);
      }
      if (
        newCollaborators.length === 0 &&
        Number.isInteger(selectedCollaborator)
      ) {
        setSelectedCollaborator(null);
      }
      if (
        Number.isInteger(selectedCollaborator) &&
        newCollaborators.length - 1 < selectedCollaborator!
      ) {
        setSelectedCollaborator(
          newCollaborators.length > 0 ? newCollaborators.length - 1 : null
        );
      }
      setAvailableCollaborators(newCollaborators);
    } else {
      if (availableCollaborators.length > 0) {
        setSelectedCollaborator(null);
        setAvailableCollaborators([]);
      }
    }
    resizeTextarea();
  };

  // detects mentions and filters collaborators in dropdown
  // also resizes textarea
  const onChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleValueChange(value);
  };

  // appends mentioned user to input area
  const applyMention = (user: ColCollaborator) => {
    if (user && user.username) {
      const message = inputValue.replace(/@\w*$/, `@${user.username}, `);
      handleValueChange(message);
      setAvailableCollaborators([]);
      setSelectedCollaborator(null);
      inputRef.current?.focus();
      setTimeout(() => {
        resizeTextarea();
      }, 0);
    }
  };

  // on text change resizes textarea
  const resizeTextarea = () => {
    // text jumps without this hack
    setTimeout(() => {
      if (inputRef && inputRef.current && inputRef.current.style) {
        inputRef.current.style.height = "5px";
        inputRef.current.style.height = inputRef.current.scrollHeight + "px";
      }
    }, 0);
  };

  useEffect(() => {
    if (editingComment) {
      const { body, visibility: commentVisibility } = editingComment;
      const message = body.trim().replaceAll(/@\[\w*\]/gm, (id) => {
        const user = collaborators.find((user) => "@[" + user._id + "]" === id);
        if (!user) return "";
        return `@${user.username}`;
      });

      handleValueChange(message);
      setVisibility(
        commentVisibility === COLLABORATION_VISIBILITY.EXTERNAL
          ? COLLABORATION_VISIBILITY.EXTERNAL
          : COLLABORATION_VISIBILITY.INTERNAL
      );
    } else {
      handleValueChange("");
      setVisibility(COLLABORATION_VISIBILITY.INTERNAL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingComment]);

  // on plane icon click or enter press
  const sendMessage = async () => {
    // remove trailing spaces and format mentions
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let message = replaceUserTags(inputValue, collaborators as any);

    if (message.length === 0) return;

    //if user tagged without any text, deleting comma
    if (
      message.length !== 1 &&
      message.slice(Math.max(0, message.indexOf(","))).length === 1
    ) {
      message = message.slice(0, Math.max(0, message.length - 1));
    }

    handleValueChange("");
    // just in case ;)
    setAvailableCollaborators([]);
    resizeTextarea();
    onMessageSend(message, visibility);

    trackEvent({
      eventName: "collab-send-comment",
      metadata: {
        type: visibility,
      },
      useServices: ["AMP"],
    });

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).hj("event", visibilityCopy.analyticsEvent);
      console.log("Hotjar trigger", visibilityCopy.analyticsEvent);
    } catch (error) {
      console.error("Hotjar fire event error:", error);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      return handleEnterPress();
    }
    // if has collaborators list open and pressed up or down
    const arrowPressed = ["ArrowUp", "ArrowDown"].includes(event.key);
    if (arrowPressed && availableCollaborators.length > 0) {
      handleArrowsPress();
    }

    // if pressed tab or space with selectedcollaborator
    const tabOrSpacePressed = ["Tab", " "].includes(event.key);
    if (tabOrSpacePressed && Number.isInteger(selectedCollaborator)) {
      event.preventDefault();
      applyMention(availableCollaborators[selectedCollaborator!]);
    }

    function handleEnterPress() {
      event.preventDefault();
      if (Number.isInteger(selectedCollaborator)) {
        applyMention(availableCollaborators[selectedCollaborator!]);
      } else {
        sendMessage();
      }
    }

    //
    function handleArrowsPress() {
      let newIndex;
      switch (event.key) {
        case "ArrowUp": {
          // if can go up
          if (selectedCollaborator && selectedCollaborator > 0) {
            newIndex = selectedCollaborator - 1;
            setSelectedCollaborator(newIndex);
            // scroll view to selected item
            if (
              mentionListRef.current &&
              mentionListRef.current.scrollTop > newIndex * MENTION_ITEM_HEIGHT
            )
              mentionListRef.current.scrollTop -= MENTION_ITEM_HEIGHT;
          }
          break;
        }
        case "ArrowDown": {
          // if can go down
          if (
            selectedCollaborator &&
            selectedCollaborator < availableCollaborators.length - 1
          ) {
            newIndex = selectedCollaborator + 1;
            setSelectedCollaborator(newIndex);
            // scroll view to selected item
            if (
              mentionListRef.current &&
              mentionListRef.current.scrollTop + 2 * MENTION_ITEM_HEIGHT <
                newIndex * MENTION_ITEM_HEIGHT
            )
              mentionListRef.current.scrollTop += MENTION_ITEM_HEIGHT;
          }
          break;
        }
      }
      event.preventDefault();
    }
  };

  const handleMentionButtonClick = () => {
    if (disabled) return;
    const textarea = inputRef.current;
    if (!textarea) return;

    const {
      selectionStart = inputValue.length,
      selectionEnd = inputValue.length,
    } = textarea;
    const newValue =
      inputValue.slice(0, selectionStart) +
      "@" +
      inputValue.slice(selectionEnd);

    handleValueChange(newValue);

    setTimeout(() => {
      textarea.focus();
      const caretPosition = selectionStart + 1;
      textarea.setSelectionRange(caretPosition, caretPosition);
    }, 0);
  };

  const handleVisibilityToggle = (checked: boolean) => {
    if (
      currentUser?.role === "collaborator" &&
      currentUser?.preferences?.canCreateExternalComments === false
    ) {
      return;
    }
    setVisibility(
      checked
        ? COLLABORATION_VISIBILITY.EXTERNAL
        : COLLABORATION_VISIBILITY.INTERNAL
    );
  };

  const isEditing = Boolean(editingComment);

  const isSendDisabled = inputValue.trim().length === 0 || disabled;
  const isCollaboratorWithoutExternalAccess =
    currentUser?.role === "collaborator" &&
    currentUser?.preferences?.canCreateExternalComments === false;
  const isSwitchDisabled = disabled || isEditing;
  const isToggleDisabled =
    isSwitchDisabled || isCollaboratorWithoutExternalAccess;
  const adminEmail = currentUser?.adminEmail ?? currentUser?.email ?? "";
  const restrictedToggleTooltip = adminEmail
    ? `To turn on External note, contact your admin ${adminEmail}`
    : "To turn on External note, contact your admin.";

  const isExternal = visibility === COLLABORATION_VISIBILITY.EXTERNAL;

  const visibilityActionLabel = isExternal
    ? isEditing
      ? "Update external note as "
      : "Send an external note as "
    : isEditing
      ? "Update internal comment as "
      : "Send an internal comment as ";

  const visibilityToggleControl = (
    <label
      htmlFor={toggleId}
      className={cn(
        "mb-0 flex min-w-0 items-center gap-[8px] rounded-[4px]",
        "bg-[var(--collab-note-bg)] px-[6px] py-[6px] text-[10px] leading-[14px] text-[#333] font-[400]",
        isToggleDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      )}
    >
      <SwitchPrimitive.Root
        id={toggleId}
        checked={visibility === COLLABORATION_VISIBILITY.EXTERNAL}
        onCheckedChange={handleVisibilityToggle}
        disabled={isToggleDisabled}
        className={cn(
          "relative inline-flex h-[20px] w-[36px] shrink-0 items-center rounded-full border border-transparent bg-[var(--color-neutral-200)] p-[2px]",
          "transition-[background-color,border-color] duration-200 ease-linear",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--collab-accent,rgba(79,49,255,0.45))] focus-visible:ring-offset-white",
          "data-[state=checked]:border-transparent data-[state=checked]:bg-[var(--collab-accent)]",
          "data-[state=checked]:!bg-[var(--collab-accent)]",
          "data-[state=unchecked]:border data-[state=unchecked]:border-[var(--color-neutral-200)]",
          "data-[state=unchecked]:!bg-[var(--collab-surface)]",
          "data-[state=unchecked]:!border-[var(--collab-border)]",
          "disabled:cursor-not-allowed disabled:opacity-60"
        )}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            "pointer-events-none block h-[16px] w-[16px] rounded-full bg-white shadow-sm",
            "transition-transform duration-200 ease-linear",
            "data-[state=checked]:translate-x-[16px] data-[state=unchecked]:translate-x-0"
          )}
        />
      </SwitchPrimitive.Root>
      <span className="truncate">
        {visibilityActionLabel}
        <span className="font-semibold text-[#1f1f1f]">{displayName}</span>
      </span>
    </label>
  );

  const internalThemeClasses = cn(
    "[--collab-accent:var(--color-blue-500)]",
    "[--collab-accent-hover:var(--color-blue-50)]",
    "[--collab-surface:var(--color-blue-25)]",
    "[--collab-border:var(--color-blue-100)]",
    "[--collab-border-focus:var(--color-blue-300)]",
    "[--collab-note-bg:var(--color-blue-50)]",
    "[--collab-note-text:var(--color-blue-500)]"
  );

  const externalThemeClasses = cn(
    "[--collab-accent:var(--color-warning-500)]",
    "[--collab-accent-hover:var(--color-warning-50)]",
    "[--collab-surface:var(--color-warning-25)]",
    "[--collab-border:var(--color-warning-100)]",
    "[--collab-border-focus:var(--color-warning-300)]",
    "[--collab-note-bg:var(--color-warning-50)]",
    "[--collab-note-text:var(--color-warning-700)]"
  );

  return (
    <div
      data-visibility={visibility}
      className={cn(
        "mt-[8px]",
        "relative flex flex-col gap-[12px]",
        "bg-[var(--color-shades-white)]",
        "[font-family:var(--font-inter)]",
        isExternal ? externalThemeClasses : internalThemeClasses
      )}
    >
      <div className="relative flex flex-col gap-[8px]">
        {availableCollaborators.length > 0 && (
          <ul
            ref={mentionListRef}
            className={cn(
              "m-0 list-none",
              "absolute bottom-[calc(100%+2px)] left-0 right-0 z-[5]",
              "max-h-[180px] overflow-auto",
              "rounded-[8px] border border-[var(--collab-border)]",
              "bg-[var(--color-shades-white)]"
            )}
          >
            {availableCollaborators.map((user, index) => (
              <li
                key={user._id}
                onClick={() => applyMention(user)}
                onMouseEnter={() => setSelectedCollaborator(index)}
                className={cn(
                  "m-0 list-none",
                  "flex items-center gap-[8px]",
                  "px-[8px] py-[8px]",
                  "cursor-pointer",
                  "text-[14px] leading-[20px] text-[#333]",
                  index === selectedCollaborator
                    ? "bg-[var(--collab-surface)]"
                    : "hover:bg-[var(--collab-surface)]"
                )}
              >
                <span>{user.username}</span>
              </li>
            ))}
          </ul>
        )}

        <textarea
          onChange={onChange}
          ref={inputRef}
          value={inputValue}
          placeholder="Type a message..."
          onKeyDown={(e) => handleKeyDown(e)}
          disabled={disabled}
          className={cn(
            "border border-[var(--collab-border)]",
            "rounded-[8px]",
            "min-h-[72px] max-h-[160px]",
            "px-[8px] py-[8px]",
            "resize-none",
            "bg-[var(--collab-surface)]",
            "text-[12px] leading-[16px] text-[#333]",
            "transition-[border-color,box-shadow,background] duration-200 ease-linear",
            "focus:outline-none",
            "disabled:bg-[var(--color-neutral-50)] disabled:cursor-not-allowed"
          )}
        />
      </div>

      <div className="relative flex w-full items-center gap-[12px]">
        <Tooltip text="Mention a team member by using @." side="bottom">
          <Button
            type="button"
            size="icon"
            variant="blue-secondary"
            className={cn(
              "h-[28px] w-[28px] rounded-[6px] border border-[var(--collab-border)] p-[4px]",
              "!bg-[var(--collab-surface)] !text-[var(--collab-accent)]",
              "hover:!bg-[var(--collab-accent-hover)]",
              disabled &&
                "!cursor-not-allowed !opacity-50 hover:!bg-[var(--collab-surface)] hover:!text-[var(--collab-accent)]"
            )}
            onClick={handleMentionButtonClick}
            disabled={disabled}
          >
            <span className="text-[14px] font-semibold leading-none">@</span>
          </Button>
        </Tooltip>

        {isCollaboratorWithoutExternalAccess ? (
          <Tooltip text={restrictedToggleTooltip} side="bottom">
            {visibilityToggleControl}
          </Tooltip>
        ) : (
          visibilityToggleControl
        )}

        {isEditing && (
          <Button
            size="xs"
            type="button"
            variant="blue-secondary"
            className="ml-auto"
            onClick={() => onCancelEdit?.()}
          >
            Cancel
          </Button>
        )}

        <Button
          size="xs"
          type="button"
          onClick={sendMessage}
          disabled={isSendDisabled}
          variant={isExternal ? "orange-primary" : "blue-primary"}
          className={cn(isEditing ? "" : "ml-auto")}
        >
          {isEditing ? "Save" : "Send"}
        </Button>
      </div>
    </div>
  );
}
export default CollaborationInputControls;
